import { useEffect, useState } from 'react';
import { useRouter, useTranslation } from '@sms/plasma-ui';
import { booksApi } from "../../services";
import { booksFeatures } from '../../utils/booksFeatures';

type BookData = { icon: string; name: string; features: string[]; settingsPermission: boolean };

export function usePageWithMenu(isBooksMenu?: boolean) {
  const { route } = useRouter('route');
  const { t } = useTranslation();
  const [bookData, setBookData] = useState<BookData>({ icon: "", name: "", features: [], settingsPermission: false });
  const [bookLoading, setBookLoading] = useState(false);
  const [hasBookPermission, setHasBookPermission] = useState(isBooksMenu ?? false);

  useEffect(() => {
    if (isBooksMenu) return;
    const bookId = route.params?.bookId ?? "master";

    setHasBookPermission(true);

    if (bookId === "master") {
      setBookData({ icon: "books", name: t("books.card.master.book.title"), features: [booksFeatures.ALL], settingsPermission: false });
      return;
    }

    const storageBookData = `book_data_${bookId}`;
    const cached = localStorage.getItem(storageBookData);
    const parsedCache = cached ? JSON.parse(cached) : null;
    if (parsedCache) setBookData(parsedCache);

    setBookLoading(true);
    booksApi.getBook(Number(bookId))
      .then((data) => {
        const bookIcon = data?.Icon ?? "";
        const bookName = data?.Name ?? "";
        const bookFeatures = data?.Features ?? [];
        const bookSettingsPermission = data?.IsOwner ? true : data?.PermissionLevel !== "Viewer" ? true : false;
        const newBookData = { icon: bookIcon, name: bookName, features: bookFeatures, settingsPermission: bookSettingsPermission };
        let updateCache = false;

        if (bookIcon !== parsedCache?.icon) {
          setBookData((prev) => ({ ...prev, icon: bookIcon }));
          updateCache = true;
        }

        if (bookName !== parsedCache?.name) {
          setBookData((prev) => ({ ...prev, name: bookName }));
          updateCache = true;
        }

        if (JSON.stringify(bookFeatures) !== JSON.stringify(parsedCache?.features)) {
          setBookData((prev) => ({ ...prev, features: bookFeatures }));
          updateCache = true;
        }

        if (bookSettingsPermission !== parsedCache?.settingsPermission) {
          setBookData((prev) => ({ ...prev, settingsPermission: bookSettingsPermission }));
          updateCache = true;
        }

        if (updateCache) localStorage.setItem(storageBookData, JSON.stringify(newBookData));
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          setHasBookPermission(false);
        }
      })
      .finally(() => {
        setBookLoading(false);
      });
  }, [route.params?.bookId, isBooksMenu]);

  return {
    bookData,
    bookLoading,
    hasBookPermission,
  };
}
